/**
 * PixiJS scene-graph scaffold for the world canvas.
 *
 * Creates the v8 `Application` + the fixed layer hierarchy the rest of the
 * renderer populates. Layer order is z-order (back-to-front):
 *
 *   tileLayer          — floor sprites
 *   floorOverlayLayer  — biome-transition overlays (Phase 1.5+), wear marks
 *   propLayer          — floor props, grid-snapped
 *   characterLayer     — avatars; D7 fills this in, depth-sorted per §4.7
 *   wallLayer          — walls + doors (drawn after floor props so occlusion
 *                         is correct)
 *   weatherLayer       — rain/snow particle overlays (D8)
 *   fogLayer           — per-player fog-of-war (D14)
 *   overlayLayer       — HUD, build-mode previews, speech bubbles (D11)
 *
 * `characterLayer` is a `sortableChildren` container so avatars + other
 * entities can re-order themselves via `depthSortKey()` without the renderer
 * manually re-parenting them each frame.
 */

import { Application, Container } from 'pixi.js';

export interface SceneLayers {
  tileLayer: Container;
  caveTileLayer: Container;
  floorOverlayLayer: Container;
  propLayer: Container;
  characterLayer: Container;
  wallLayer: Container;
  weatherLayer: Container;
  fogLayer: Container;
  overlayLayer: Container;
}

export interface SceneGraph {
  /** PixiJS v8 `Application` — owns the WebGL context + ticker. */
  app: Application;
  /** Root world container — everything game-world transforms as a unit
   *  under this so the camera's pan/zoom sets `worldContainer.position`
   *  and `worldContainer.scale`. */
  worldContainer: Container;
  layers: SceneLayers;
  /** Destroy the `Application` + all children. Idempotent. */
  destroy: () => void;
}

/**
 * Mount a PixiJS v8 Application into `host` and build the layer hierarchy.
 * The returned `SceneGraph` is what every rendering subsystem (D6/D7/D8/D14)
 * appends into. Callers invoke `destroy()` on React StrictMode unmount.
 */
export async function createSceneGraph(
  host: HTMLElement,
  options: { background?: string; antialias?: boolean } = {},
): Promise<SceneGraph> {
  const app = new Application();
  await app.init({
    background: options.background ?? '#111111',
    resizeTo: host,
    antialias: options.antialias ?? true,
  });
  host.appendChild(app.canvas);

  const worldContainer = new Container({ label: 'world' });
  // Place world origin at viewport centre; camera controller keeps it there.
  worldContainer.position.set(app.screen.width / 2, app.screen.height / 2);
  app.stage.addChild(worldContainer);

  const layers: SceneLayers = {
    tileLayer: new Container({ label: 'tiles' }),
    caveTileLayer: new Container({ label: 'cave-tiles' }),
    floorOverlayLayer: new Container({ label: 'floor-overlay' }),
    propLayer: new Container({ label: 'props' }),
    characterLayer: new Container({
      label: 'characters',
      sortableChildren: true,
    }),
    wallLayer: new Container({ label: 'walls' }),
    weatherLayer: new Container({ label: 'weather' }),
    fogLayer: new Container({ label: 'fog' }),
    overlayLayer: new Container({ label: 'overlay' }),
  };

  // World-space layers — these pan + zoom with the camera.
  worldContainer.addChild(
    layers.tileLayer,
    layers.caveTileLayer,
    layers.floorOverlayLayer,
    layers.propLayer,
    layers.characterLayer,
    layers.wallLayer,
    layers.fogLayer,
  );

  // Screen-space layers — weather + HUD overlay ride above the camera
  // transform so rain doesn't pan with the world and speech bubbles /
  // hover highlights stay on-screen.
  app.stage.addChild(layers.weatherLayer, layers.overlayLayer);

  const onResize = (): void => {
    worldContainer.position.set(app.screen.width / 2, app.screen.height / 2);
  };
  app.renderer.on('resize', onResize);

  let destroyed = false;
  return {
    app,
    worldContainer,
    layers,
    destroy: () => {
      if (destroyed) return;
      destroyed = true;
      app.renderer.off('resize', onResize);
      if (app.canvas.parentNode === host) {
        host.removeChild(app.canvas);
      }
      app.destroy(true, { children: true, texture: true });
    },
  };
}
